import { Spacing, Typography } from '@persona-id/design-tokens';
import { StylesConfig } from 'react-select';
import { DefaultTheme } from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';

import { OptionType } from '../ReactSelect';

// We pass {} to customStyles because we don't want original styles over-
// writing our component styles.
export const SelectMdStyles = (
  customStyles: StylesConfig<OptionType, boolean>,
  hasError: boolean,
  portal: boolean,
  theme: DefaultTheme,
): StylesConfig<OptionType, boolean> => ({
  ...customStyles,
  container: (styles, state) => ({
    ...styles,
    width: '100%',

    ...(customStyles.container != null && customStyles.container({}, state)),
  }),
  control: (styles, state) => ({
    ...styles,
    borderRadius: parseInt(theme.element.borderRadius, 10),
    borderColor:
      state.menuIsOpen || state.isFocused ? theme.color.border.active : theme.color.border.input,
    width: '100%',
    height: '32px',
    minHeight: 'auto',
    boxShadow: state.isFocused ? `0 0 0 1px ${theme.color.border.active}` : 'none',
    '&:hover': {
      boxShadow:
        state.menuIsOpen || state.isFocused
          ? `0 0 0 1px ${theme.color.border.active}, ${Elevation.Mid}`
          : `0 0 0 0, ${Elevation.Mid}`,
    },
    transition: TransitionDuration.Fast,
    // TODO when there's a way to enable scroll and disable remove together
    ...(hasError
      ? {
          borderColor: theme.color.variants.danger.default,
          color: theme.color.variants.danger.default,
          boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}`,
          '&:hover': {
            boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}, ${Elevation.Mid}`,
          },
        }
      : undefined),
    ...(customStyles.control != null && customStyles.control({}, state)),
  }),
  indicatorsContainer: (styles, state) => ({
    ...styles,
    height: '30px',
    marginRight: Spacing[2],
    ...(customStyles.indicatorsContainer != null && customStyles.indicatorsContainer({}, state)),
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    display: 'none',
    ...(customStyles.indicatorSeparator != null && customStyles.indicatorSeparator({}, state)),
  }),
  menu: (styles, state) => ({
    ...styles,
    boxShadow: Elevation.High,
    ...(customStyles.menu != null && customStyles.menu({}, state)),
  }),
  menuList: (styles, state) => ({
    ...styles,
    padding: Spacing[2],
    ...(customStyles.menuList != null && customStyles.menuList({}, state)),
  }),
  menuPortal: (styles, state) => ({
    ...styles,
    ...(portal ? { zIndex: Z_INDICES.modalDropdown } : {}),
    ...(customStyles.menuPortal != null && customStyles.menuPortal({}, state)),
  }),
  option: (styles, state) => {
    const baseStyles = {
      ...styles,
      backgroundColor: state.isFocused ? theme.color.background.highlight : 'transparent',
      borderRadius: theme.element.borderRadius,
      display: 'flex',
      alignItems: 'center',
      padding: `${Spacing[3]} ${Spacing[2]} ${Spacing[3]} ${Spacing[3]}`,
      fontWeight: theme.text.paragraph.md.fontWeight,
      transitionDuration: TransitionDuration.Fast,
      ':active': {
        backgroundColor: 'transparent',
        color: theme.color.primary.brand,
      },
    };
    const disabledStyles = state.isDisabled
      ? {
          backgroundColor: 'transparent',
          color: theme.color.text.disabled,
          ':active': {
            backgroundColor: 'transparent',
          },
          overflow: 'auto',
        }
      : {};

    return {
      ...baseStyles,
      ...disabledStyles,
      ...(customStyles.option != null && customStyles.option({}, state)),
    };
  },
  placeholder: (styles, state) => ({
    ...styles,
    color: hasError ? theme.color.variants.danger.default : theme.color.text.primary,
    ...(customStyles.placeholder != null && customStyles.placeholder({}, state)),
  }),
  valueContainer: (styles, state) => ({
    ...styles,
    ...Typography.TextMedium,
    fontSize: parseInt(theme.text.paragraph.md.fontSize, 10),
    fontWeight: theme.text.paragraph.md.fontWeight,
    fontFamily: theme.text.fontFamily.base,
    textDecoration: 'none',
    outline: '0 !important',
    padding: `0px ${Spacing[2]}`,
    ...(customStyles.valueContainer != null && customStyles.valueContainer({}, state)),
  }),
  input: (styles, state) => ({
    ...styles,
    ...(customStyles.input != null && customStyles.input({}, state)),
  }),
});

export const SelectSmStyles = (
  customStyles: StylesConfig<OptionType, boolean>,
  hasError: boolean,
  portal: boolean,
  theme: DefaultTheme,
): StylesConfig<OptionType, boolean> => ({
  ...customStyles,
  container: (styles, state) => ({
    ...styles,
    width: '100%',
    ...(customStyles.container != null && customStyles.container({}, state)),
  }),
  control: (styles, state) => ({
    ...styles,
    borderRadius: parseInt(theme.element.borderRadius, 10),
    borderColor:
      state.menuIsOpen || state.isFocused ? theme.color.border.active : theme.color.border.input,
    width: '100%',
    height: '24px', // Explicitly specify 24px for height because it is shorter than theme element height xs
    minHeight: 'auto',
    boxShadow: state.isFocused ? `0 0 0 1px ${theme.color.border.active}` : 'none',
    '&:hover': {
      boxShadow:
        state.menuIsOpen || state.isFocused
          ? `0 0 0 1px ${theme.color.border.active}, ${Elevation.Mid}`
          : `0 0 0 0, ${Elevation.Mid}`,
    },
    transition: TransitionDuration.Fast,
    ...(hasError
      ? {
          borderColor: theme.color.variants.danger.default,
          color: theme.color.variants.danger.default,
          boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}`,
          '&:hover': {
            boxShadow: `0 0 0 1px ${theme.color.variants.danger.default}, ${Elevation.Mid}`,
          },
        }
      : undefined),
    ...(customStyles.control != null && customStyles.control({}, state)),
  }),
  singleValue: (styles, state) => ({
    ...styles,
    fontSize: theme.text.paragraph.sm.fontSize,
    fontWeight: theme.text.paragraph.sm.fontWeight,
    lineHeight: theme.text.paragraph.sm.lineHeight,
    ...(customStyles.singleValue != null && customStyles.singleValue({}, state)),
  }),
  indicatorsContainer: (styles, state) => ({
    ...styles,
    marginRight: Spacing[2],
    height: '22px',
    ...(customStyles.indicatorsContainer != null && customStyles.indicatorsContainer({}, state)),
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    display: 'none',
    ...(customStyles.indicatorSeparator != null && customStyles.indicatorSeparator({}, state)),
  }),
  menu: (styles, state) => ({
    ...styles,
    boxShadow: Elevation.High,
    ...(customStyles.menu != null && customStyles.menu({}, state)),
  }),
  menuList: (styles, state) => ({
    ...styles,
    padding: Spacing[2],
    ...(customStyles.menuList != null && customStyles.menuList({}, state)),
  }),
  menuPortal: (styles, state) => ({
    ...styles,
    ...(portal ? { zIndex: Z_INDICES.modalDropdown } : {}),
    ...(customStyles.menuPortal != null && customStyles.menuPortal({}, state)),
  }),
  option: (styles, state) => {
    const baseStyles = {
      ...styles,
      backgroundColor: state.isFocused ? theme.color.background.highlight : 'transparent',
      borderRadius: theme.element.borderRadius,
      display: 'flex',
      alignItems: 'center',
      padding: Spacing[2],
      transitionDuration: TransitionDuration.Fast,
      fontSize: theme.text.paragraph.sm.fontSize,
      fontWeight: theme.text.paragraph.sm.fontWeight,
      lineHeight: theme.text.paragraph.sm.lineHeight,
      paddingLeft: Spacing[3],
      ':active': {
        // Slightly higher than focused style for mobile
        // https://github.com/JedWatson/react-select/blob/master/packages/react-select/src/components/Option.js#L66
        backgroundColor: 'transparent',
        color: theme.color.primary.brand,
      },
    };
    const disabledStyles = state.isDisabled
      ? {
          backgroundColor: 'transparent',
          color: theme.color.text.disabled,
          ':active': {
            backgroundColor: 'transparent',
          },
        }
      : {};

    return {
      ...baseStyles,
      ...disabledStyles,
      ...(customStyles.option != null && customStyles.option({}, state)),
    };
  },
  placeholder: (styles, state) => ({
    ...styles,
    color: hasError ? theme.color.variants.danger.default : theme.color.text.primary,
    ...(customStyles.placeholder != null && customStyles.placeholder({}, state)),
  }),
  valueContainer: (styles, state) => ({
    ...styles,
    fontFamily: theme.text.fontFamily.base,
    textDecoration: 'none',
    outline: '0 !important',
    height: '22px', // Account for border being set on the container
    minHeight: 'auto',
    fontSize: theme.text.paragraph.sm.fontSize,
    fontWeight: theme.text.paragraph.sm.fontWeight,
    lineHeight: theme.text.paragraph.sm.lineHeight,
    padding: `0px ${Spacing[2]}`,
    ...(customStyles.valueContainer != null && customStyles.valueContainer({}, state)),
  }),
  input: (styles, state) => ({
    ...styles,
    paddingTop: 0,
    paddingBottom: 0,
    ...(customStyles.input != null && customStyles.input({}, state)),
  }),
});
